// Trust and operating accounts, and the master escrow log.
//
// Property management firms commonly run a trust account per client alongside one
// operating account for the brokerage's own money. Illinois permits that, and it
// permits a single pooled trust account with a per-client ledger inside it. What
// the rule actually requires either way is the journal, the per-transaction
// ledger, a monthly reconciliation, and a master log naming every account and the
// bank holding it — so the log is a first-class view here rather than a report
// nobody remembers to run.
//
// The log reports its own gaps. A tidy list with a quietly empty column is worse
// than a list that says what is missing, because the first gets trusted.

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Field, Modal, Input, Select, Textarea,
    Button, Notice, Loading, Empty, money, fmtDate, titleCase, statusTone,
} from '../../components/secure/pm/PMKit';

const TYPES = [
    { value: 'trust_brokerage', label: 'Brokerage escrow — the company’s own special account' },
    { value: 'trust_pooled', label: 'Trust — pooled (all property management clients, per-client ledger inside)' },
    { value: 'trust_client', label: 'Trust — single client' },
    { value: 'trust_security_deposit', label: 'Trust — security deposits' },
    { value: 'operating', label: 'Operating (the brokerage’s own money)' },
];

const TYPE_TONE = {
    trust_brokerage: 'good', trust_pooled: 'indigo', trust_client: 'violet',
    trust_security_deposit: 'warn', operating: 'slate', other: 'slate',
};

// Three different jobs, and putting them in one undifferentiated list is how the
// company's transaction escrow ended up filed as somebody's rent account.
//
//   brokerage   earnest money and deposits on SALES and LEASE transactions. The
//               special account a brokerage has whether or not it manages
//               property. Belongs to the company escrow module; shown here
//               because the master log covers every escrow account.
//   client      rent and deposits collected for OWNERS, ledger per client.
//   operating   the brokerage's own earned money. Outside 1450.750 entirely.
const GROUPS = [
    { key: 'client', title: 'Property management client trusts',
      note: 'Rent and security deposits held for owners. Reconciled monthly, per client.',
      types: ['trust_pooled', 'trust_client', 'trust_security_deposit'] },
    { key: 'brokerage', title: 'Brokerage escrow — company',
      note: 'Earnest money and deposits on sales and lease transactions. Managed under Escrow, not here; '
          + 'shown because 1450.750(i)(5) puts every escrow account on the master log.',
      types: ['trust_brokerage', 'other'] },
    { key: 'operating', title: 'Operating',
      note: 'The brokerage’s own earned money. Outside 1450.750 — no escrow reconciliation is owed.',
      types: ['operating'] },
];

export default function PMTrustAccountsPage() {
    const apiClient = useApiClient();
    const [tab, setTab] = useState('accounts');
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [owners, setOwners] = useState([]);
    const [log, setLog] = useState(null);
    const [detail, setDetail] = useState(null);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [a, o] = await Promise.all([
                apiClient.get('/api/property-management/trust-accounts'),
                apiClient.get('/api/property-management/owners'),
            ]);
            setAccounts(a || []); setOwners(o || []);
            try { setLog(await apiClient.get('/api/property-management/trust-accounts/master-log')); }
            catch { /* managing broker only */ }
        } catch (e) { setError({ message: e.message }); } finally { setLoading(false); }
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const act = async (fn, successTone = 'good') => {
        setBusy(true); setError(null);
        try {
            const r = await fn();
            setModal(null); setForm({});
            await load();
            if (r?.note) setError({ tone: 'warn', title: 'Created', message: r.note });
            else if (r?.message) setError({ tone: successTone, message: r.message });
        } catch (e) {
            setError({ message: e.message,
                problems: e.payload?.alternative ? [`Alternative: ${e.payload.alternative}`] : undefined });
        } finally { setBusy(false); }
    };

    // The close dialog starts from the server's own readiness check, so it can
    // say exactly what would be refused before anyone types a reason.
    const [closeCheck, setCloseCheck] = useState(null);
    const openClose = async (acct) => {
        setForm({ account_id: acct.account_id, account_name: acct.account_name });
        setCloseCheck(null); setModal('close');
        try { setCloseCheck(await apiClient.get(`/api/property-management/trust-accounts/${acct.account_id}/close-check`)); }
        catch (e) { setModal(null); setError({ message: e.message }); }
    };

    const open = async (id) => {
        try { setDetail(await apiClient.get(`/api/property-management/trust-accounts/${id}`)); setModal('detail'); }
        catch (e) { setError({ message: e.message }); }
    };

    if (loading) return <Loading />;

    const trust = accounts.filter((a) => a.account_type.startsWith('trust_')
        && a.account_type !== 'trust_brokerage');
    const brokerage = accounts.filter((a) => a.account_type === 'trust_brokerage');
    const pending = accounts.filter((a) => a.status === 'pending');
    const trustTotal = trust.filter((a) => a.status !== 'closed')
        .reduce((s, a) => s + Number(a.balance || 0), 0);

    return (
        <div>
            <PageHeader
                title="Trust Accounts"
                subtitle="A trust account per client, a pooled one, or both — Illinois permits either. What it requires is the journal, the per-transaction ledger, monthly reconciliation, and this log."
            >
                <Button onClick={() => { setForm({ account_type: 'trust_client', is_interest_bearing: 0 }); setModal('new'); }}>
                    Open account
                </Button>
            </PageHeader>

            {error && <Notice tone={error.tone || 'bad'} title={error.title}
                message={error.message} problems={error.problems} onDismiss={() => setError(null)} />}

            {pending.length > 0 && (
                <Notice tone="warn"
                    title={`${pending.length} account${pending.length === 1 ? '' : 's'} pending an account number`}
                    message="Money should not be received into an account whose number is not on file — it cannot be reconciled or listed on the master log." />
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <Stat label="Client trusts" value={trust.filter((a) => a.status !== 'closed').length}
                    sub={`${trust.filter((a) => a.account_type === 'trust_client').length} client-specific`} />
                <Stat label="Client funds held" value={money(trustTotal)} tone="indigo"
                    sub="property management only" />
                <Stat label="Brokerage escrow" value={brokerage.length}
                    sub="company — managed under Escrow" />
                <Stat label="Master log"
                    value={log ? (log.complete ? 'Complete' : `${log.gaps.length} gap${log.gaps.length === 1 ? '' : 's'}`) : '—'}
                    tone={log ? (log.complete ? 'good' : 'warn') : 'default'} />
            </div>

            <Tabs tabs={[
                { key: 'accounts', label: 'Accounts', count: accounts.length },
                { key: 'log', label: 'Master escrow log' },
            ]} active={tab} onChange={setTab} />

            {tab === 'accounts' && GROUPS.map((g) => {
                const rows = accounts.filter((a) => g.types.includes(a.account_type));
                if (!rows.length) return null;
                return (
                <div key={g.key} className="mb-5">
                <Panel title={g.title} subtitle={g.note}>
                    <Table
                        columns={[
                            { key: 'account_name', label: 'Account',
                                render: (r) => (
                                    <div>
                                        <div className="font-medium text-slate-800">{r.account_name}</div>
                                        <div className="text-xs text-slate-400">
                                            {r.bank_name}
                                            {r.account_number
                                                ? ` · ${r.account_number}`
                                                : <span className="text-amber-600"> · no number on file</span>}
                                        </div>
                                    </div>
                                ) },
                            { key: 'account_type', label: 'Type', nowrap: true,
                                render: (r) => (
                                    <div>
                                        <Pill tone={TYPE_TONE[r.account_type]}>{titleCase(r.account_type)}</Pill>
                                        {r.owner_name && <div className="text-xs text-slate-500 mt-1">{r.owner_name}</div>}
                                    </div>
                                ) },
                            { key: 'property_count', label: 'Properties', align: 'right' },
                            { key: 'balance', label: 'Balance', align: 'right',
                                render: (r) => money(r.balance) },
                            { key: 'last_reconciled', label: 'Reconciled', nowrap: true,
                                // The operating account holds earned compensation and is
                                // outside 1450.750. Showing it as overdue for an escrow
                                // reconciliation it does not owe is a false alarm that
                                // teaches people to ignore the real ones.
                                render: (r) => r.account_type === 'operating'
                                    ? <span className="text-xs text-slate-400">not an escrow account</span>
                                    : r.last_reconciled
                                        ? fmtDate(r.last_reconciled)
                                        : <Pill tone={r.entry_count ? 'bad' : 'slate'}>
                                            {r.entry_count ? 'never' : 'no activity'}</Pill> },
                            { key: 'status', label: 'Status', nowrap: true,
                                render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                        ]}
                        rows={rows.map((a) => ({ ...a, _key: a.account_id }))}
                        onRowClick={(r) => open(r.account_id)}
                        rowTone={(r) => (r.status === 'pending' ? 'warn'
                            : (r.account_type !== 'operating' && r.entry_count && !r.last_reconciled) ? 'bad' : null)}
                        empty="None."
                    />
                </Panel>
                </div>
                );
            })}
            {tab === 'accounts' && accounts.length === 0 && (
                <Panel><Empty title="No accounts yet"
                    message="Client money cannot be received until a trust account exists." /></Panel>
            )}

            {tab === 'log' && (
                log ? (
                    <Panel title="Master escrow account log"
                        subtitle={log.authority}
                        actions={<Button variant="ghost" onClick={() => window.print()}>Print</Button>}>
                        {!log.complete && (
                            <Notice tone="warn" title="The log is incomplete"
                                problems={log.gaps.map((g) => `${g.account_name}: missing ${g.missing.join(', ')}`)} />
                        )}
                        <Table
                            columns={[
                                { key: 'account_name', label: 'Account' },
                                { key: 'bank_name', label: 'Bank',
                                    render: (r) => (
                                        <div>
                                            <div>{r.bank_name}</div>
                                            <div className="text-xs text-slate-400">
                                                {r.bank_address || <span className="text-amber-600">address not on file</span>}
                                            </div>
                                        </div>
                                    ) },
                                { key: 'account_number', label: 'Number', nowrap: true,
                                    render: (r) => r.account_number
                                        ? <code className="text-xs">{r.account_number}</code>
                                        : <Pill tone="bad">missing</Pill> },
                                { key: 'owner_name', label: 'Held for',
                                    render: (r) => r.owner_name || (r.is_pooled ? 'Pooled — all clients' : '—') },
                                { key: 'is_interest_bearing', label: 'Interest', nowrap: true,
                                    render: (r) => r.is_interest_bearing
                                        ? <Pill tone="warn">bearing</Pill> : <Pill>non-bearing</Pill> },
                                { key: 'opened_on', label: 'Opened', nowrap: true, render: (r) => fmtDate(r.opened_on) },
                                { key: 'balance', label: 'Balance', align: 'right', render: (r) => money(r.balance) },
                            ]}
                            rows={log.accounts.map((a) => ({ ...a, _key: a.account_id }))}
                            empty="No escrow accounts recorded."
                        />
                    </Panel>
                ) : (
                    <Panel><Empty title="Managing broker only"
                        message="The master escrow log is produced for the Division by the managing broker." /></Panel>
                )
            )}

            {/* ---------------- open an account ---------------- */}
            {modal === 'new' && (
                <Modal title="Open an account"
                    subtitle="Opening an account that will hold client money is the managing broker's act (68 IAC 1450.705)."
                    onClose={() => setModal(null)} wide>
                    <Select label="Type" value={form.account_type}
                        onChange={(e) => setForm({ ...form, account_type: e.target.value, owner_id: '' })}
                        options={TYPES.filter((t) => t.value !== 'trust_brokerage')}
                        hint="The company’s own escrow account is opened under Escrow, not here." />
                    {form.account_type === 'trust_client' && (
                        <Select label="Client" value={form.owner_id || ''}
                            onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                            options={[{ value: '', label: 'Select…' },
                                ...owners.map((o) => ({ value: o.owner_id, label: o.legal_name }))]}
                            hint="A client trust account must name the client whose funds it holds." />
                    )}
                    {form.account_type === 'operating' && (
                        <Notice tone="info"
                            message="An operating account holds the brokerage's own money. It cannot be attached to a client — keeping the two apart is what 1450.750 exists for." />
                    )}
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Input label="Account name" value={form.account_name || ''}
                            onChange={(e) => setForm({ ...form, account_name: e.target.value })} />
                        <Input label="Bank" value={form.bank_name || ''}
                            onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
                        <Input label="Bank street address" value={form.bank_address || ''}
                            onChange={(e) => setForm({ ...form, bank_address: e.target.value })}
                            className="sm:col-span-2"
                            hint="Required on the master escrow log — a city alone will show as a gap." />
                        <Input label="Depository state (2 letters)" value={form.depository_state || ''}
                            onChange={(e) => setForm({ ...form, depository_state: e.target.value.toUpperCase().slice(0, 2) })}
                            hint="Florida requires escrow at a depository located and doing business in Florida." />
                        <label className="block text-sm mb-3"><span className="text-slate-700">Type of depository</span>
                            <select value={form.depository_kind || ''} onChange={(e) => setForm({ ...form, depository_kind: e.target.value })}
                                className="mt-1 block w-full border rounded p-2">
                                <option value="">—</option><option value="bank">Bank</option><option value="credit_union">Credit union</option>
                                <option value="savings_association">Savings association</option><option value="trust_company">Trust company</option>
                                <option value="title_company">Title company</option>
                            </select></label>
                        <Input label="Account number" value={form.account_number || ''}
                            onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                            hint="Leave blank if it is not to hand. The account opens as PENDING." />
                        <Input label="Opened on" type="date" value={form.opened_on || ''}
                            onChange={(e) => setForm({ ...form, opened_on: e.target.value })} />
                        <Select label="Interest bearing" value={form.is_interest_bearing ?? 0}
                            onChange={(e) => setForm({ ...form, is_interest_bearing: Number(e.target.value) })}
                            options={[{ value: 0, label: 'No — the default under 1450.750' },
                                { value: 1, label: 'Yes — required by law or directed in writing' }]} />
                        <Input label="Service charge cushion" type="number" step="0.01"
                            value={form.service_charge_cushion ?? ''}
                            onChange={(e) => setForm({ ...form, service_charge_cushion: e.target.value })}
                            hint="Broker funds kept in to avoid charges. Must be documented and no more than the bank's minimum." />
                        <Input label="Authorized signers" value={form.authorized_signers || ''}
                            onChange={(e) => setForm({ ...form, authorized_signers: e.target.value })}
                            className="sm:col-span-2" />
                    </div>
                    <Textarea label="Purpose / notes" value={form.notes || ''}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => act(() => apiClient.post('/api/property-management/trust-accounts', form))}>
                            {busy ? 'Opening…' : 'Open account'}</Button>
                    </div>
                </Modal>
            )}

            {/* ---------------- detail ---------------- */}
            {modal === 'detail' && detail && (
                <Modal title={detail.account_name}
                    subtitle={`${detail.bank_name} · ${titleCase(detail.account_type)}`}
                    onClose={() => { setModal(null); setDetail(null); }} wide>
                    <div className="grid sm:grid-cols-2 gap-x-8 mb-5">
                        <dl>
                            <Field label="Bank" value={detail.bank_name} />
                            <Field label="Bank address" value={detail.bank_address}
                                hint="Required on the master escrow log." />
                            <Field label="Account number" value={detail.account_number} />
                            <Field label="Held for" value={detail.owner_name
                                || (detail.is_pooled ? 'Pooled — all clients, with a per-client ledger' : null)} />
                        </dl>
                        <dl>
                            <Field label="Status" value={<Pill tone={statusTone(detail.status)}>{titleCase(detail.status)}</Pill>} />
                            <Field label="Opened" value={fmtDate(detail.opened_on)} />
                            <Field label="Interest bearing" value={detail.is_interest_bearing ? 'Yes' : 'No'} />
                            <Field label="Authorized signers" value={detail.authorized_signers} />
                        </dl>
                    </div>

                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                        Properties funded through this account
                    </h4>
                    <Table
                        columns={[
                            { key: 'name', label: 'Property' },
                            { key: 'city', label: 'City' },
                            { key: 'status', label: 'Status', render: (r) => titleCase(r.status) },
                        ]}
                        rows={(detail.properties || []).map((p) => ({ ...p, _key: p.pm_property_id }))}
                        empty="None yet."
                    />

                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-6 mb-2">
                        Reconciliations
                    </h4>
                    <Table
                        columns={[
                            { key: 'statement_date', label: 'Statement', nowrap: true,
                                render: (r) => fmtDate(r.statement_date) },
                            { key: 'bank_balance', label: 'Bank', align: 'right', render: (r) => money(r.bank_balance) },
                            { key: 'owner_books_total', label: 'Owner books', align: 'right',
                                render: (r) => money(r.owner_books_total) },
                            { key: 'difference', label: 'Difference', align: 'right',
                                render: (r) => money(r.difference) },
                            { key: 'status', label: '', nowrap: true,
                                render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                        ]}
                        rows={(detail.reconciliations || []).map((r) => ({ ...r, _key: r.pm_reconciliation_id }))}
                        empty="Never reconciled. 1450.750 requires it monthly, within ten days of the statement."
                    />

                    <div className="flex flex-wrap justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
                        <Button variant="ghost"
                            onClick={() => { setForm({ ...detail, account_number: '' }); setModal('edit'); }}>
                            Edit</Button>
                        {detail.status === 'closed'
                            ? <Button variant="ghost"
                                onClick={() => { setForm({ account_id: detail.account_id, account_name: detail.account_name }); setModal('reopen'); }}>
                                Reopen…</Button>
                            : <Button variant="ghost" onClick={() => openClose(detail)}>
                                Close…</Button>}
                        <Button variant="danger"
                            onClick={() => act(() => apiClient.delete(
                                `/api/property-management/trust-accounts/${detail.account_id}`))}>
                            Delete</Button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2 text-right">
                        Delete only works on an account that never held an entry. Anything else closes.
                    </p>
                </Modal>
            )}

            {modal === 'edit' && (
                <Modal title={`Edit ${form.account_name}`} onClose={() => setModal(null)} wide>
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Input label="Account name" value={form.account_name || ''}
                            onChange={(e) => setForm({ ...form, account_name: e.target.value })} />
                        <Input label="Bank" value={form.bank_name || ''}
                            onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
                        <Input label="Bank street address" value={form.bank_address || ''}
                            onChange={(e) => setForm({ ...form, bank_address: e.target.value })}
                            className="sm:col-span-2" />
                        <Input label="Depository state (2 letters)" value={form.depository_state || ''}
                            onChange={(e) => setForm({ ...form, depository_state: e.target.value.toUpperCase().slice(0, 2) })}
                            hint="Florida requires escrow at a depository located and doing business in Florida." />
                        <label className="block text-sm mb-3"><span className="text-slate-700">Type of depository</span>
                            <select value={form.depository_kind || ''} onChange={(e) => setForm({ ...form, depository_kind: e.target.value })}
                                className="mt-1 block w-full border rounded p-2">
                                <option value="">—</option><option value="bank">Bank</option><option value="credit_union">Credit union</option>
                                <option value="savings_association">Savings association</option><option value="trust_company">Trust company</option>
                                <option value="title_company">Title company</option>
                            </select></label>
                        <Input label="Account number" value={form.account_number || ''}
                            onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                            hint="Blank leaves the stored number unchanged. Supplying one takes a pending account active." />
                        <Input label="Opened on" type="date" value={form.opened_on || ''}
                            onChange={(e) => setForm({ ...form, opened_on: e.target.value })} />
                        <Input label="Authorized signers" value={form.authorized_signers || ''}
                            onChange={(e) => setForm({ ...form, authorized_signers: e.target.value })}
                            className="sm:col-span-2" />
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={() => {
                            const { account_id, ...body } = form;
                            if (!body.account_number) delete body.account_number;
                            act(() => apiClient.put(`/api/property-management/trust-accounts/${account_id}`, body));
                        }}>{busy ? 'Saving…' : 'Save'}</Button>
                    </div>
                </Modal>
            )}

            {modal === 'close' && (
                <Modal title={`Close ${form.account_name || 'this account'}`}
                    subtitle="Managing broker only. Read what closing does before you confirm."
                    onClose={() => setModal(null)} wide>
                    {!closeCheck ? <Loading /> : (
                        <>
                            <Notice tone="info" title="What closing does" message={closeCheck.consequence} />
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Checks</h4>
                            <ul className="mb-4 space-y-2">
                                {closeCheck.checks.map((c) => (
                                    <li key={c.key} className="flex gap-3 text-sm">
                                        <Pill tone={c.ok ? 'good' : c.blocking ? 'bad' : 'warn'}>
                                            {c.ok ? 'ok' : c.blocking ? 'blocks' : 'warning'}</Pill>
                                        <div>
                                            <div className="font-medium text-slate-800">{c.label}</div>
                                            <div className="text-xs text-slate-500">{c.detail}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {!closeCheck.can_close ? (
                                <Notice tone="bad" title="This account cannot be closed yet"
                                    message="Fix the items marked “blocks” first. The server refuses the close until they are clear." />
                            ) : (
                                <>
                                    <Textarea label="Reason (required)" value={form.reason || ''}
                                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                        placeholder="e.g. Client portfolio transferred to another brokerage; funds remitted 30 Sep" />
                                    <Input label="Closed on" type="date" value={form.closed_on || ''}
                                        onChange={(e) => setForm({ ...form, closed_on: e.target.value })} />
                                    {closeCheck.checks.some((c) => c.requires_acknowledgement) && (
                                        <label className="flex gap-2 items-start text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3 mb-3">
                                            <input type="checkbox" className="mt-1" checked={!!form.acknowledge_unreconciled}
                                                onChange={(e) => setForm({ ...form, acknowledge_unreconciled: e.target.checked })} />
                                            <span>I am closing this account without a balanced reconciliation of its final statement.
                                                The reason above explains why; this is recorded against my name.</span>
                                        </label>
                                    )}
                                    <Input label={`Type the account name to confirm: ${closeCheck.account_name}`}
                                        value={form.confirm_name || ''}
                                        onChange={(e) => setForm({ ...form, confirm_name: e.target.value })} />
                                </>
                            )}
                            <div className="flex justify-end gap-2 mt-4">
                                <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                                {closeCheck.can_close && (
                                    <Button variant="danger"
                                        disabled={busy || !String(form.reason || '').trim()
                                            || form.confirm_name !== closeCheck.account_name
                                            || (closeCheck.checks.some((c) => c.requires_acknowledgement) && !form.acknowledge_unreconciled)}
                                        onClick={() => act(() => apiClient.post(
                                            `/api/property-management/trust-accounts/${form.account_id}/close`,
                                            { reason: form.reason, closed_on: form.closed_on || undefined,
                                              acknowledge_unreconciled: !!form.acknowledge_unreconciled }))}>
                                        {busy ? 'Closing…' : 'Close account'}</Button>
                                )}
                            </div>
                        </>
                    )}
                </Modal>
            )}

            {modal === 'reopen' && (
                <Modal title={`Reopen ${form.account_name}`}
                    subtitle="Managing broker only. The account goes back on the master log as active and can receive and pay client money again."
                    onClose={() => setModal(null)}>
                    <Textarea label="Reason (required — recorded in the account's audit trail)" value={form.reason || ''}
                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        placeholder="e.g. Closed in error; the client's renewal was signed 2 Oct" />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy || !String(form.reason || '').trim()}
                            onClick={() => act(() => apiClient.post(
                                `/api/property-management/trust-accounts/${form.account_id}/reopen`,
                                { reason: form.reason }))}>
                            {busy ? 'Reopening…' : 'Reopen account'}</Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
